'use client';

import { useMemo, useState } from 'react';

import { BaseCalendarType } from '../type/BaseCalendarType';

interface CalendarDatesProps {
  baseDate: Date | undefined;
  baseCalendarAmount?: number;
  disabled?: { from: Date; to: Date } | { before: Date; after: Date };
}

export const useCalendarDates = ({ baseDate, disabled, baseCalendarAmount }: CalendarDatesProps) => {
  const [renderedDate, setRenderedDate] = useState<Date>(baseDate || new Date());
  const [numberOfMonths, setNumberOfMonths] = useState<number>(baseCalendarAmount || 3);

  const isDisabledRange = (disabled: BaseCalendarType['disabled']): disabled is { from: Date; to: Date } => {
    return disabled !== undefined && 'from' in disabled && 'to' in disabled;
  };

  const isDisabledBoundary = (disabled: BaseCalendarType['disabled']): disabled is { before: Date; after: Date } => {
    return disabled !== undefined && 'before' in disabled && 'after' in disabled;
  };

  const handleNextMonth = () => {
    setRenderedDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1));
  };

  const handlePrevMonth = () => {
    setRenderedDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1));
  };

  const handleNextYear = () => {
    if (isDisabledBoundary(disabled)) {
      const limitedYear = disabled.after.getFullYear();
      const limitedMonth = disabled.after.getMonth();

      setRenderedDate((prev) => new Date(prev.getFullYear() + 1, prev.getFullYear() + 1 === limitedYear ? limitedMonth : prev.getMonth()));
      return;
    }
    setRenderedDate((prev) => new Date(prev.getFullYear() + 1, prev.getMonth()));
  };

  const handlePrevYear = () => {
    if (isDisabledBoundary(disabled)) {
      const limitedYear = disabled.before.getFullYear();
      const limitedMonth = disabled.before.getMonth();

      setRenderedDate((prev) => new Date(prev.getFullYear() - 1, prev.getFullYear() - 1 === limitedYear ? limitedMonth : prev.getMonth()));
      return;
    }

    setRenderedDate((prev) => new Date(prev.getFullYear() - 1, prev.getMonth()));
  };

  const loadPrevCalendar = (amountMonths: number) => {
    setRenderedDate((prev) => {
      const year = prev.getFullYear();
      const month = prev.getMonth();
      const date = prev.getDate();

      return new Date(year, month - amountMonths, date);
    });

    setNumberOfMonths((prev) => {
      return prev + amountMonths;
    });
  };

  const loadNextCalendar = (amountMonths: number) => {
    setNumberOfMonths((prev) => {
      return prev + amountMonths;
    });
  };

  const isPrevYearButtonDisabled = useMemo(() => {
    if (isDisabledBoundary(disabled)) {
      const isLastYear = disabled?.before?.getFullYear();

      return renderedDate.getFullYear() === isLastYear;
    }
    return false;
  }, [renderedDate, disabled]);

  const isPrevMonthButtonDisabled = useMemo(() => {
    if (isDisabledBoundary(disabled)) {
      const isLastYear = disabled?.before?.getFullYear();
      const isLastMonth = disabled?.before?.getMonth();

      return renderedDate.getFullYear() === isLastYear && renderedDate.getMonth() <= isLastMonth;
    }
    return false;
  }, [renderedDate, disabled]);

  const isNextYearButtonDisabled = useMemo(() => {
    if (isDisabledBoundary(disabled)) {
      const isLatestYear = disabled?.after?.getFullYear();

      return renderedDate.getFullYear() === isLatestYear;
    }
    return false;
  }, [renderedDate, disabled]);

  const isNextMonthButtonDisabled = useMemo(() => {
    if (isDisabledBoundary(disabled)) {
      const isLatestYear = disabled?.after?.getFullYear();
      const isLatestMonth = disabled?.after?.getMonth();

      return renderedDate.getFullYear() === isLatestYear && renderedDate.getMonth() >= isLatestMonth;
    }
    return false;
  }, [renderedDate, disabled]);

  return {
    numberOfMonths,
    setNumberOfMonths,
    renderedDate,
    setRenderedDate,
    isPrevMonthButtonDisabled,
    isPrevYearButtonDisabled,
    isNextMonthButtonDisabled,
    isNextYearButtonDisabled,
    handleNextYear,
    handlePrevYear,
    handleNextMonth,
    handlePrevMonth,
    loadNextCalendar,
    loadPrevCalendar,
    isDisabledBoundary,
    isDisabledRange
  };
};
